import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assertAndContinue} from 'Asserts';
import {ResidueType} from 'biojs/BioConstants';
import {formatPolymerResidueCount} from 'biojs/Util';
import {ActionOrigin, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  BulkAssemblyMethod,
  PreviewConstruct,
  IMMERSIVE_MODAL_PROPS,
  AaSequencePolymerInfo,
  SequencePolymerInfo,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {PolymerModalContent} from 'molbio/bulk-assembly/polymer-map-modals/PolymerModalContent';
import {PolymerTitle} from 'molbio/bulk-assembly/polymer-map-modals/PolymerTitle';
import SequenceMapContainer, {
  AssemblySequenceType,
} from 'molbio/bulk-assembly/polymer-map-modals/SequenceMapContainer';
import BulkAssemblyPrimersTable from 'molbio/bulk-assembly/tables/BulkAssemblyPrimersTable';
import AaSequenceMapContainer from 'molbio/maps/standalone/AaSequenceMapContainer';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {Tab, TabContainer} from 'ui-library/TabContainer';

export enum ConstructModalTab {
  POLYMER = 'polymer',
  PRIMERS = 'primers',
}

export interface ConstructModalProps {
  assemblyId: string;
  initialConstructCid: string;
  initialTab?: ConstructModalTab;
  onRequestClose: () => void;
  constructData: Array<ConstructData>;
  bulkAssemblyMethod: BulkAssemblyMethod;
  canCreatePrimers: boolean;
  isReadOnly: boolean;
}

interface ConstructData extends PreviewConstruct {
  polymer: null | SequencePolymerInfo | AaSequencePolymerInfo;
}

interface ConstructModalContentsProps {
  assemblyId: string;
  bulkAssemblyMethod: BulkAssemblyMethod;
  canCreatePrimers: boolean;
  initialConstructCid: string;
  initialTab: ConstructModalTab;
  constructData: Array<ConstructData>;
  isReadOnly: boolean;
}

function ConstructModalContents({
  assemblyId,
  bulkAssemblyMethod,
  canCreatePrimers,
  initialConstructCid,
  initialTab,
  constructData,
  isReadOnly,
}: ConstructModalContentsProps): JSX.Element {
  const [activeIndex, setActiveIndex] = useState(
    constructData.findIndex((construct) => construct.cid === initialConstructCid)
  );
  const construct = constructData[activeIndex];
  const constructCid = construct.cid;
  const [activeTab, setActiveTab] = useState<ConstructModalTab>(initialTab);

  const errorBanner = construct.hasErrors ? (
    <div className={css(styles.errorBanner)}>
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text={<strong>This construct is invalid. It cannot be assembled as currently specified.</strong>}
        supportingInfo={
          <span>
            To review and fix the relevant error(s), go to the <strong>Overview</strong> tab.
          </span>
        }
        action={{actionType: InlineBannerActionType.ACCORDION}}
      />
    </div>
  ) : null;

  const constructPolymer = construct.polymer;
  assertAndContinue(
    Boolean(errorBanner || constructPolymer != null),
    'Expected either a polymer or an error to be present in construct validation results.'
  );
  return (
    <PolymerModalContent
      assemblyId={assemblyId}
      activeCardSize={80}
      activeIndex={activeIndex}
      inactiveCardSize={80}
      setActiveIndex={setActiveIndex}
      cards={constructData.map((construct) => ({
        content: (
          <>
            <div className={css(styles.condensedCardHeader)}>
              <PolymerTitle polymerId={construct.outputPolymerId} polymerName={construct.name} />
            </div>
            <div>
              {construct.visualPolymer?.length != null && constructPolymer != null
                ? formatPolymerResidueCount(
                    construct.visualPolymer?.length,
                    constructPolymer.kind === 'sequence' ? ResidueType.NUCLEOTIDE : ResidueType.AA
                  )
                : 'no length'}
            </div>
          </>
        ),
        polymerId: null,
      }))}
      activePolymerMap={
        <TabContainer<ConstructModalTab>
          onRequestTabChange={(tab: ConstructModalTab) => {
            analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_CONSTRUCT_MODAL_ACTIVE_TAB, {
              actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_CONSTRUCT_MODAL,
              assemblyId,
              tab,
            });
            setActiveTab(tab);
          }}
          activeTab={activeTab}
          className={css(styles.tabContainer)}
          navClassName={css(styles.nav)}
          paneClassName={css(styles.tabPane)}
          // Sets up the pane itself as a column-oriented flexbox.
          isFullHeight={true}
        >
          <Tab tabKey={ConstructModalTab.POLYMER} title="Sequence">
            {errorBanner}
            {constructPolymer != null && constructPolymer.kind === 'sequence' && (
              <SequenceMapContainer
                sequence={constructPolymer.ref.current}
                bulkAssemblyMethod={bulkAssemblyMethod}
                type={AssemblySequenceType.CONSTRUCT}
              />
            )}
            {constructPolymer != null && constructPolymer.kind === 'protein' && (
              <AaSequenceMapContainer protein={constructPolymer.ref.current} />
            )}
          </Tab>
          {canCreatePrimers && (
            <Tab tabKey={ConstructModalTab.PRIMERS} title="Primers">
              {errorBanner}
              <div
                className={css(styles.primerTabContainer)}
                {...getTestProps({component: 'BulkAssemblyConstructPrimerTab', element: 'root'})}
              >
                {construct.primers.length > 0 ? (
                  <BulkAssemblyPrimersTable
                    assemblyId={assemblyId}
                    constructCid={constructCid}
                    primers={construct.primers}
                    isReadOnly={isReadOnly}
                  />
                ) : (
                  <p>No primers associated with this construct.</p>
                )}
              </div>
            </Tab>
          )}
        </TabContainer>
      }
      activePolymerHasParts={false}
    />
  );
}

export default function ConstructModal({
  assemblyId,
  initialConstructCid,
  initialTab = ConstructModalTab.POLYMER,
  constructData,
  canCreatePrimers,
  bulkAssemblyMethod,
  onRequestClose,
  isReadOnly,
}: ConstructModalProps): JSX.Element {
  return (
    <Modal
      {...IMMERSIVE_MODAL_PROPS}
      title="View constructs"
      onRequestClose={onRequestClose}
      buttons={[{text: 'Done', submit: true, style: ButtonStyle.SECONDARY, onClick: onRequestClose}]}
    >
      <ConstructModalContents
        assemblyId={assemblyId}
        bulkAssemblyMethod={bulkAssemblyMethod}
        canCreatePrimers={canCreatePrimers}
        initialConstructCid={initialConstructCid}
        initialTab={initialTab}
        constructData={constructData}
        isReadOnly={isReadOnly}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  errorBanner: {
    margin: 12,
    marginTop: 16,
  },
  tabContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  nav: {
    padding: 12,
  },
  tabPane: {
    marginTop: 0,
    flexGrow: 1,
  },
  primerTabContainer: {
    padding: 16,
    paddingRight: 32,
    overflowY: 'auto',
  },
  condensedCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '8px',
  },
});
